import * as THREE from 'three';
import { clamp, damp, dampAngle } from './math';

/** Third-person orbit camera that follows a target, or a first-person view from the eyes. */
export class CameraRig {
  yaw = 0;
  pitch = 0.38;
  distance = 8;
  minDistance = 3.2;
  maxDistance = 16;
  /** Indoors the camera looks down into the room from above the cut-away walls. */
  minPitch = 0.08;
  useOccluders = true;
  /** First person: the camera sits at the eyes and looks where you point it. */
  firstPerson = false;
  /** First-person look angle above the horizon (radians). */
  lookPitch = 0;
  private readonly focus = new THREE.Vector3();
  private readonly look = new THREE.Vector3();
  private readonly ray = new THREE.Ray();
  private readonly hit = new THREE.Vector3();
  private readonly dir = new THREE.Vector3();
  private initialized = false;
  /** Current (possibly collision-shortened) boom length. */
  private boom = 8;
  occluders: readonly THREE.Box3[] = [];

  constructor(readonly camera: THREE.PerspectiveCamera) {}

  /** Forward direction on the ground plane (from camera toward target). */
  forward(out = new THREE.Vector3()): THREE.Vector3 {
    return out.set(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
  }

  right(out = new THREE.Vector3()): THREE.Vector3 {
    return out.set(Math.cos(this.yaw), 0, -Math.sin(this.yaw));
  }

  orbit(dx: number, dy: number, zoom: number): void {
    if (this.firstPerson) {
      this.yaw -= dx * 0.0042;
      this.lookPitch = clamp(this.lookPitch - dy * 0.0042, -1.35, 1.35);
      return;
    }
    this.yaw -= dx * 0.005;
    this.pitch = clamp(this.pitch + dy * 0.004, this.minPitch, 1.25);
    this.distance = clamp(this.distance + zoom * 0.9, this.minDistance, this.maxDistance);
  }

  snap(): void {
    this.initialized = false;
  }

  update(
    dt: number,
    target: THREE.Vector3,
    opts: {
      height?: number;
      sway?: number;
      shake?: THREE.Vector3;
      followYaw?: number;
      time?: number;
      /** Where the eyes are, for first person. */
      eye?: THREE.Vector3;
    } = {},
  ): void {
    const height = opts.height ?? 1.5;
    if (this.firstPerson && opts.eye) {
      this.updateFirstPerson(dt, opts.eye, opts);
      return;
    }
    if (opts.followYaw !== undefined) this.yaw = dampAngle(this.yaw, opts.followYaw, 2.5, dt);
    if (!this.initialized) {
      this.focus.copy(target);
      this.initialized = true;
    } else {
      this.focus.x = damp(this.focus.x, target.x, 10, dt);
      this.focus.y = damp(this.focus.y, target.y, 6, dt);
      this.focus.z = damp(this.focus.z, target.z, 10, dt);
    }
    const cp = Math.cos(this.pitch);
    const c = this.camera;
    this.look.set(this.focus.x, this.focus.y + height, this.focus.z);
    this.dir.set(Math.sin(this.yaw) * cp, Math.sin(this.pitch), Math.cos(this.yaw) * cp);
    // Pull the camera in if a big structure sits between it and the player.
    let allowed = this.distance;
    this.ray.set(this.look, this.dir);
    for (const box of this.useOccluders ? this.occluders : []) {
      if (box.containsPoint(this.look)) continue;
      const p = this.ray.intersectBox(box, this.hit);
      if (p) allowed = Math.min(allowed, Math.max(1.2, this.look.distanceTo(p) - 0.35));
    }
    this.boom = allowed < this.boom ? allowed : damp(this.boom, allowed, 4, dt);
    c.position.copy(this.look).addScaledVector(this.dir, this.boom);
    if (c.position.y < 0.6) c.position.y = 0.6;
    if (opts.shake) c.position.add(opts.shake);
    c.lookAt(this.look);
    this.applySway(opts.sway ?? 0, opts.time ?? 0);
  }

  private updateFirstPerson(
    dt: number,
    eye: THREE.Vector3,
    opts: { sway?: number; shake?: THREE.Vector3; followYaw?: number; time?: number },
  ): void {
    // Behind the wheel the view swings with the car, but you can still glance around.
    if (opts.followYaw !== undefined) this.yaw = dampAngle(this.yaw, opts.followYaw, 6, dt);
    const c = this.camera;
    c.position.copy(eye);
    if (opts.shake) c.position.add(opts.shake);
    const cp = Math.cos(this.lookPitch);
    this.look.set(
      c.position.x - Math.sin(this.yaw) * cp,
      c.position.y + Math.sin(this.lookPitch),
      c.position.z - Math.cos(this.yaw) * cp,
    );
    c.lookAt(this.look);
    this.applySway(opts.sway ?? 0, opts.time ?? 0);
    this.initialized = false;
  }

  private applySway(sway: number, t: number): void {
    if (sway <= 0) return;
    this.camera.rotateZ(Math.sin(t * 1.1) * 0.06 * sway);
    this.camera.rotateX(Math.sin(t * 0.7) * 0.02 * sway);
  }
}
